import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import { setToastSink, type ToastApi, type ToastType } from './toast-bus';
import './toast.css';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  exiting: boolean;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Lỗi cần đọc kỹ hơn nên ở lâu gấp đôi. */
const DURATION_MS: Record<ToastType, number> = { success: 1500, info: 1500, error: 3000 };
const EXIT_ANIMATION_MS = 350;

/** id tăng dần — ổn định hơn Math.random() và không đụng nhau khi bắn dồn. */
let nextId = 0;

const ICONS: Record<ToastType, string> = {
  success: 'M4.5 12.75l6 6 9-13.5',
  error: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z',
  info: 'M11.25 11.25l.041-.02a.75.75 0 111.063.852l-.708 2.836a.75.75 0 001.063.852l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  /**
   * Mọi hẹn giờ đang chạy — để dọn khi provider unmount.
   *
   * Không giữ handle thì timer sống tiếp sau khi cây React đã tháo, rồi gọi
   * `setToasts` trên một component không còn tồn tại và giữ closure lại trong bộ
   * nhớ. Ở app này provider nằm ở gốc nên hiếm, nhưng nó là timer duy nhất trong
   * web chưa được dọn — và hot-reload lúc dev thì unmount thật.
   */
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const later = useCallback((fn: () => void, ms: number) => {
    const handle = setTimeout(() => {
      timers.current.delete(handle);
      fn();
    }, ms);
    timers.current.add(handle);
  }, []);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
      later(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, EXIT_ANIMATION_MS);
    },
    [later],
  );

  const push = useCallback(
    (message: string, type: ToastType, durationMs?: number) => {
      const id = nextId++;
      setToasts((prev) => [...prev, { id, message, type, exiting: false }]);
      later(() => {
        dismiss(id);
      }, durationMs ?? DURATION_MS[type]);
    },
    [dismiss, later],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message, duration) => {
        push(message, 'success', duration);
      },
      error: (message, duration) => {
        push(message, 'error', duration);
      },
      info: (message, duration) => {
        push(message, 'info', duration);
      },
    }),
    [push],
  );

  // Mở cầu cho code ngoài React (interceptor axios) bắn toast.
  useEffect(() => {
    setToastSink(api);
    return () => {
      setToastSink(null);
    };
  }, [api]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-container">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast-item toast-item-${toast.type} ${toast.exiting ? 'toast-item-exiting' : ''}`}
          >
            <div className="toast-icon">
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d={ICONS[toast.type]} />
              </svg>
            </div>
            <div className="toast-message">{toast.message}</div>
            <button
              type="button"
              onClick={() => {
                dismiss(toast.id);
              }}
              className="toast-close-btn"
              aria-label="Đóng"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast phải nằm trong ToastProvider');
  return ctx;
}
